import Autocomplete, { AutocompleteProps, createFilterOptions } from '@material-ui/lab/Autocomplete';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';

import { useCustomFieldStyles } from '@/components/shared/components/NewContextForms/hooks/useCustomFieldStyles';
import {
  CustomTextField,
  CustomTextFieldProps,
} from '@/components/shared/components/NewContextForms/shared/CustomTextField';
import { ReadOnlyDataRow } from '@/components/shared/components/NewContextForms/shared/ReadOnlyDataRow';

interface AutocompleteFormFieldProps<TOption, TFieldValues extends FieldValues>
  extends CustomTextFieldProps<TFieldValues> {
  name: string;
  optionList: TOption[];
  getInputDisplay?: (option: TOption) => string;
  getListDisplay?: (option: TOption) => string;
  getSearchValue?: (option: TOption) => string;
  autocompleteProps?: Partial<AutocompleteProps<TOption, false, false | true, false>>;
}

export const AutocompleteFormField = <TOption, TFieldValues extends FieldValues>({
  name,
  optionList,
  getInputDisplay,
  getListDisplay,
  getSearchValue,
  autocompleteProps,
  ...customTextFieldProps
}: AutocompleteFormFieldProps<TOption, TFieldValues>) => {
  const control = useFormContext()?.control;
  const { getMUIAutocompleteFormClasses } = useCustomFieldStyles({
    fieldVariant: customTextFieldProps.fieldVariant,
    disabled: customTextFieldProps.disabled,
  });

  const autocompleteClasses = getMUIAutocompleteFormClasses();

  if (!control) {
    return <ReadOnlyDataRow {...customTextFieldProps} />;
  }

  const isOptionOfTypeObject = typeof optionList[0] === 'object';

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        return (
          <>
            <Autocomplete
              classes={autocompleteClasses}
              openOnFocus
              value={
                field.value
                  ? isOptionOfTypeObject
                    ? { ...field.value }
                    : field.value
                  : customTextFieldProps.value || null
              }
              options={optionList}
              getOptionLabel={getInputDisplay}
              filterOptions={createFilterOptions({ stringify: getSearchValue })}
              renderOption={getListDisplay}
              renderInput={(params) => <CustomTextField {...params} error={error} {...customTextFieldProps} />}
              disabled={customTextFieldProps.disabled}
              getOptionSelected={(option, value) =>
                isOptionOfTypeObject ? JSON.stringify(option) === JSON.stringify(value) : option === value
              }
              {...autocompleteProps}
              onChange={(_, value, reason) => {
                if (value) {
                  autocompleteProps?.onChange && autocompleteProps.onChange(_, value, reason);
                  field.onChange(value);
                } else {
                  autocompleteProps?.onChange && autocompleteProps.onChange(_, value, reason);
                  field.onChange(isOptionOfTypeObject ? null : '');
                }
              }}
            />
          </>
        );
      }}
    />
  );
};
